<template>
  <div class="event-catalog-russian-exporter">
    <div class="event-catalog-russian-exporter__container">
      <h1 class="event-catalog-russian-exporter__title">
        {{ $t("event.catalog.modal.russianExporterList.title") }}
      </h1>

      <p class="event-catalog-russian-exporter__text">
        {{ $t("event.catalog.modal.russianExporterList.subtitle") }}<br />
        <i>{{ $t("event.catalog.modal.russianExporterList.primary") }}</i>
      </p>
    </div>

    <div class="event-catalog-russian-exporter__container">
      <button
        v-if="russianExporterList?.length"
        class="event-catalog-russian-exporter__print-rubrics event-catalog-russian-exporter__print-rubrics--top"
        @click="printRubrics"
      >
        <icon name="print" />
        {{ $t("event.catalog.modal.russianExporterList.printRubrics") }}
      </button>

      <SearchField
        :placeholder="
          $t('event.catalog.modal.russianExporterList.searchPlaceholder')
        "
        class="event-catalog-russian-exporter__search"
        v-model="searchText"
      />

      <template v-if="exportersBySearch.length">
        <div
          class="item-event-catalog-russian-exporter__item"
          v-for="item in exportersBySearch"
          :key="item.id"
        >
          <Checkbox
            v-model="exporterModel[item.id]"
            :disabled="!item.is_selected"
          >
            {{ item.title }}
          </Checkbox>
        </div>
      </template>

      <template v-if="exportersBySearch.length === 0">
        <div
          class="item-event-catalog-russian-exporter"
          v-for="exporter in russianExporterList"
          :key="exporter.id"
        >
          <div
            class="item-event-catalog-russian-exporter__header"
            @click="toggleExporter(exporter)"
          >
            <div class="item-event-catalog-russian-exporter__header-checkbox">
              <Checkbox
                v-model="exporterModel[exporter.id]"
                :disabled="!exporter.is_selected"
              />

              <span
                class="item-event-catalog-russian-exporter__header-checkbox-text"
              >
                {{ exporter.code }}.
                {{
                  i18n.locale === "en" ? exporter.name_eng : exporter.name_rus
                }}
              </span>
            </div>

            <span
              class="item-event-catalog-russian-exporter__header-count"
              v-if="countSelected(exporter)"
            >
              {{ countSelected(exporter) }}
            </span>

            <div
              class="item-event-catalog-russian-exporter__header-toggle"
              v-if="exporter.children?.length"
            >
              <icon
                name="arrow-down"
                :class="{ _rotated: exporterOpened === exporter.id }"
              />
            </div>
          </div>

          <div
            class="item-event-catalog-russian-exporter__content"
            v-if="exporter.children?.length && exporterOpened === exporter.id"
          >
            <div
              class="item-event-catalog-russian-exporter__item"
              v-for="inner in exporter.children"
              :key="inner.id"
            >
              <Checkbox
                v-model="exporterModel[inner.id]"
                :disabled="!inner.is_selected"
              >
                {{ inner.code }}.
                {{ i18n.locale === "en" ? inner.name_eng : inner.name_rus }}
              </Checkbox>

              <template v-if="inner.children?.length">
                <template v-for="sub in inner.children">
                  <div
                    v-if="sub"
                    :key="sub.id"
                    class="item-event-catalog-russian-exporter__item"
                  >
                    <Checkbox
                      v-model="exporterModel[sub.id]"
                      :disabled="!sub.is_selected"
                    >
                      {{ sub.code }}.
                      {{ i18n.locale === "en" ? sub.name_eng : sub.name_rus }}
                    </Checkbox>

                    <template v-if="sub.children?.length">
                      <template v-for="fourthSub in sub.children">
                        <div
                          v-if="fourthSub"
                          :key="fourthSub.id"
                          class="item-event-catalog-russian-exporter__item"
                        >
                          <Checkbox
                            v-model="exporterModel[fourthSub.id]"
                            :disabled="!fourthSub.is_selected"
                          >
                            {{ fourthSub.code }}.
                            {{
                              i18n.locale === "en"
                                ? fourthSub.name_eng
                                : fourthSub.name_rus
                            }}
                          </Checkbox>

                          <template v-if="fourthSub.children?.length">
                            <template v-for="fifthSub in fourthSub.children">
                              <div
                                v-if="fifthSub"
                                :key="fifthSub.id"
                                class="item-event-catalog-russian-exporter__item"
                              >
                                <Checkbox
                                  v-model="exporterModel[fifthSub.id]"
                                  :disabled="!fifthSub.is_selected"
                                >
                                  {{ fifthSub.code }}.
                                  {{
                                    i18n.locale === "en"
                                      ? fifthSub.name_eng
                                      : fifthSub.name_rus
                                  }}
                                </Checkbox>
                              </div>
                            </template>
                          </template>
                        </div>
                      </template>
                    </template>
                  </div>
                </template>
              </template>
            </div>
          </div>
        </div>
      </template>
    </div>

    <div
      v-if="russianExporterList?.length"
      class="event-catalog-russian-exporter__suggest-rubric-link"
    >
      <button
        class="event-catalog-russian-exporter__print-rubrics"
        @click="printRubrics"
      >
        <icon name="print" />
        {{ $t("event.catalog.modal.russianExporterList.printRubrics") }}
      </button>
    </div>
  </div>
</template>

<script>
import { mapGetters, mapState } from "vuex";
import printJS from "print-js";
import apiCatalog from "@/api/catalog";
import { SearchField, Checkbox } from "@v2/shared/ui/components";
import { i18n } from "@v2/shared/config";

function isStrContains(str1, str2) {
  return str1.toLowerCase().indexOf(str2.toLowerCase()) !== -1;
}

function searchExporters(text, exporters) {
  let output = [];

  exporters.forEach(exporter => {
    if (isStrContains(exporter.title, text)) {
      output.push({
        id: exporter.id,
        title: exporter.title,
        is_selected: exporter.is_selected
      });
    }

    if (exporter.children) {
      output = [...output, ...searchExporters(text, exporter.children)];
    }
  });

  return output;
}

export default {
  name: "EventModalCatalogRussianExporter",

  components: {
    SearchField,
    Checkbox
  },

  data() {
    return {
      searchText: "",
      exporterModel: {},
      exporterOpened: "",

      urlPrintRubrics: null,
      i18n: i18n
    };
  },

  computed: {
    ...mapState("event", ["event"]),

    ...mapGetters("eventCatalog", [
      "freeRussianExporterCount",
      "getOrderDataFromCatalog",
      "russianExporterList"
    ]),

    ...mapGetters("event", ["eventId"]),

    selectedRussianExporterId() {
      return Object.keys(this.exporterModel).filter(
        id => this.exporterModel[id]
      );
    },

    exportersBySearch() {
      return this.searchText
        ? searchExporters(this.searchText, this.russianExporterList)
        : [];
    }
  },

  watch: {
    selectedRussianExporterId: {
      deep: true,
      handler() {
        this.$store.dispatch(
          "eventCatalog/updateRussianExportersList",
          this.selectedRussianExporterId
        );
      }
    }
  },

  methods: {
    countSelected(item) {
      let count = 0;

      if (this.exporterModel[item.id]) {
        count++;
      }

      if (item.children) {
        item.children.forEach(x => {
          count += this.countSelected(x);
        });
      }

      return count;
    },

    toggleExporter(exporter) {
      if (this.exporterOpened === exporter.id) {
        this.exporterOpened = null;
      } else {
        this.exporterOpened = exporter.id;
      }
    },

    async printRubrics() {
      const response = await apiCatalog.downloadRubrics(this.eventId, 1);
      let blob = new Blob([response], {
        type: "application/pdf"
      });
      const url = window.URL.createObjectURL(blob);
      printJS(url);
    }
  },

  created() {
    this.searchText = "";

    const { selectedRussianExporterId } = this.$store.state.eventCatalog;

    this.exporterModel = selectedRussianExporterId.reduce((acc, id) => {
      acc[id] = true;

      return acc;
    }, {});
  }
};
</script>

<style lang="scss">
.event-catalog-russian-exporter {
  &__search {
    .search-field__input {
      background-color: transparent;
    }
  }
}
.item-event-catalog-russian-exporter {
  .checkbox__fake {
    background-color: transparent;
  }
}
</style>

<style lang="scss" scoped>
@import "@v2/app/styles/mixins";
@import "@v2/app/styles/media";

.event-catalog-russian-exporter {
  &__title {
    @include h1();
    margin-bottom: 34px;
    font-size: 20px;
    @include respond-down(md) {
      margin-bottom: 24px;
      font-size: 16px;
    }
  }

  &__text {
    @include description();
    margin-bottom: 17px;
  }

  &__suggest {
    margin-top: -32px;
    margin-bottom: -70px;

    background: rgba(#f9f9f9, 0.5);

    border-top: 2px solid #f1f1f1;
    border-bottom: 2px solid #f1f1f1;

    padding: 47px 0 48px 0;

    @include respond-down(md) {
      padding: 32px 0 32px 0;
    }

    &-title {
      @include description();

      display: flex;
      align-items: flex-start;

      margin-bottom: 20px;

      &-close {
        @include button-clean();

        margin-top: 5px;
        margin-left: 23px;

        .svg-icon {
          width: 16px;
          height: 16px;
        }
      }
    }

    &-input {
      width: 530px;

      @include respond-down(md) {
        width: 100%;
        margin-bottom: 10px;
      }
    }
  }

  &__suggest &__container {
    display: flex;
    flex-wrap: wrap;
    justify-content: space-between;
  }

  &__search {
    margin-top: 30px;
    margin-bottom: 16px;

    width: 100%;
  }

  &__footer {
    border-top: 2px solid #f1f1f1;
    margin-top: 65px;

    padding: 18px 0;

    &-list {
      display: flex;

      list-style: none;

      padding: 0;
      margin: 0;

      margin-right: auto;

      &-item {
        @include description();

        span {
          color: #000;
        }

        margin-right: 40px;
      }
    }
  }

  &__footer &__container {
    display: flex;
    align-items: center;
  }

  &__suggest-rubric-link {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-end;
    align-content: flex-start;
    align-items: center;
    margin-top: 30px;
  }

  &__print-rubrics {
    display: flex;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: flex-start;
    align-content: flex-start;
    align-items: center;
    margin: 10px 0;
    padding: 0;
    font-weight: 500;
    font-size: 14px;
    line-height: 20px;
    background-color: transparent;
    border: none;
    box-shadow: none;
    outline: none;
    color: #009ee3;
    cursor: pointer;

    @media (max-width: 739px) {
      font-size: 12px;
    }
    &--top {
      margin-top: 44px;
    }
    .svg-icon {
      max-width: 18px;
      max-height: 18px;
      margin-right: 10px;

      @media (max-width: 739px) {
        max-width: 16px;
        max-height: 16px;
      }
    }
  }
}

.item-event-catalog-russian-exporter {
  border-top: 1px solid rgba(205, 206, 208, 0.5);
  border-bottom: 1px solid rgba(205, 206, 208, 0.5);

  margin-top: -1px;

  @include respond-down(md) {
    margin-left: -16px;
    width: calc(100% + 2 * 16px);
  }

  &__header {
    display: flex;
    align-items: center;

    padding: 23px 0;

    padding-right: 24px;

    cursor: pointer;

    @include respond-down(md) {
      padding-right: 16px;
      padding-left: 16px;
    }

    &-checkbox {
      display: flex;
      align-items: flex-start;

      margin-right: auto;

      &-text {
        position: relative;
        top: 3px;

        line-height: 1.6;

        cursor: pointer;

        @include respond-down(md) {
          font-size: 12px;
        }
      }
    }

    &-count {
      color: #0070d2;
      font-size: 14px;
      font-weight: 500;
      margin-left: 20px;

      @include respond-down(md) {
        font-size: 12px;
        margin-left: 8px;

        align-self: flex-start;
        transform: translateY(7px);
      }
    }

    &-toggle {
      margin-left: 8px;

      .svg-icon {
        width: 16px;
        height: 16px;

        &._rotated {
          transform: rotate(-180deg);
        }
      }

      @include respond-down(md) {
        align-self: flex-start;

        transform: translateY(5px);
      }
    }
  }

  &__content {
    border-top: 1px solid rgba(205, 206, 208, 0.3);

    /*margin-top: 23px;*/
    padding-bottom: 22px;
  }
  &__item {
    padding-top: 23px;

    @include respond-down(md) {
      padding-right: 16px;
      padding-left: 16px;
    }
  }

  &__item > &__item {
    padding-left: 37px;
  }
}
</style>
